import type { CSSProperties } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveAvatarUrl } from "@/lib/avatarUtils";
import { getUserInitials } from "@/lib/userUtils";
import { cn } from "@/lib/utils";

/** The subset of a profile a face needs. Matches `UserData` structurally. */
export interface StackPerson {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
}

type StackSize = "sm" | "md" | "lg";

const SIZE: Record<StackSize, { avatar: string; text: string; star: string }> = {
  sm: { avatar: "h-5 w-5", text: "text-[9px]", star: "lead-star--sm" },
  md: { avatar: "h-6 w-6", text: "text-xs", star: "" },
  lg: { avatar: "h-7 w-7", text: "text-xs", star: "" },
};

interface AssigneeStackProps {
  /**
   * The people, IN THE TICKET'S OWN ORDER — `assigned_to_id[0]` first. The
   * order is the model: the first element is the primary assignee, and this
   * component draws that fact rather than deciding it. Resolving ids to
   * profiles with `.filter()` silently re-sorts them into profile order, so
   * every caller has to map over the ids instead (see `orderedAssignees`).
   */
  people: StackPerson[];
  /** Faces drawn before the rest collapse into a "+N" chip. */
  max?: number;
  size?: StackSize;
  /**
   * Whether the first face carries the primary star.
   *
   * "auto" — the default — draws it only when there is somebody to be primary
   * *over*. A ticket with one assignee has no helpers, so on the rows that are
   * most of a backlog the star would mark the only face there is: a mark the
   * reader has to learn, appearing everywhere, saying nothing. It earns its
   * place the moment a second face turns up beside it.
   *
   * "always" is for the places that have already SAID "primary" in words and
   * want the glyph to match (the assignment summary); "never" is for a stack
   * that is entirely helpers.
   */
  primaryStar?: "auto" | "always" | "never";
  className?: string;
}

/**
 * A ticket's assignees as overlapping faces, primary first.
 *
 * TWO things here are not the browser's defaults, and both are the point:
 *
 * - **The primary leads and is marked.** A tiny cyan star on the first face's
 *   shoulder (`.lead-star`, index.css) — same size as every other face, so the
 *   stack never wobbles vertically.
 * - **The stack layers backwards.** DOM order paints later siblings on top, so
 *   a plain overlapping row buries the leftmost face under all the others —
 *   exactly the one the reader is meant to read first, and the one carrying
 *   the star. Explicit descending z-index puts the primary on TOP and each
 *   face behind the one to its left.
 */
const AssigneeStack = ({ people, max = 3, size = "md", primaryStar = "auto", className }: AssigneeStackProps) => {
  if (people.length === 0) return null;

  const shown = people.slice(0, max);
  const hidden = people.length - shown.length;
  const s = SIZE[size];
  const starred = primaryStar === "always" || (primaryStar === "auto" && people.length > 1);
  // Anything drawn after the faces still has to sit under all of them.
  const base = people.length + 1;

  return (
    <div className={cn("flex min-w-0 items-center", className)}>
      {shown.map((user, i) => (
        <span
          key={user.id}
          // The wrapper, not the Avatar, carries the overlap and the lift: the
          // Avatar primitive is `overflow-hidden` (it has to be, for the round
          // crop), which would clip the star off its own corner.
          //
          // `-ml-1` (4px), not the 6px it was: at a 6px overlap the faces read
          // as one clump, and the primary's star had less of its own face to
          // sit against. 4px still reads as a stack — they overlap — with each
          // face's edge visible.
          className="relative -ml-1 shrink-0 transition-transform first:ml-0 hover:z-10 hover:-translate-y-0.5"
          style={{ zIndex: base - i } as CSSProperties}
        >
          {/* `.avatar-ring` / `.avatar-plate` (index.css): the ring is the
              ROW's colour, whatever the row is currently doing, and the plate
              is opaque so overlapping faces don't show through each other. */}
          <Avatar className={cn("avatar-ring block shrink-0 ring-2", s.avatar)}>
            {user.avatar_url && <AvatarImage src={resolveAvatarUrl(user.avatar_url)} alt="" />}
            <AvatarFallback className={cn("avatar-plate font-medium text-primary dark:text-blue-200", s.text)}>
              {getUserInitials(user)}
            </AvatarFallback>
          </Avatar>
          {i === 0 && starred && <span className={cn("lead-star", s.star)} aria-hidden />}
        </span>
      ))}
      {hidden > 0 && (
        <span
          className={cn(
            "avatar-ring -ml-1 flex shrink-0 items-center justify-center rounded-full bg-muted font-bold text-muted-foreground ring-2",
            s.avatar,
            size === "sm" ? "text-[8px]" : "text-[10px]",
          )}
          style={{ zIndex: 0 } as CSSProperties}
        >
          +{hidden}
        </span>
      )}
    </div>
  );
};

export default AssigneeStack;
